import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Target, Plus, Loader2, Calendar, CheckCircle2, Trash2, Trophy } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "../i18n";

const API = '/api';

function ChallengeCard({ challenge, onDelete, index }) {
  const { t } = useTranslation();
  const mediaTypes = [
    { value: "", label: t.challenges.allTypes },
    { value: "book", label: t.mediaTypesPlural.book },
    { value: "movie", label: t.mediaTypesPlural.movie },
    { value: "tv_series", label: t.mediaTypesPlural.tv_series },
    { value: "video_game", label: t.mediaTypesPlural.video_game },
  ];
  const progressPercent = challenge.target_count > 0 
    ? Math.min((challenge.progress / challenge.target_count) * 100, 100) 
    : 0;
  const isCompleted = challenge.completed;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
    >
      <Card 
        className={`overflow-hidden ${isCompleted ? "border-primary/50 bg-primary/5" : ""}`}
        data-testid={`challenge-${challenge.challenge_id}`}
      >
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              {isCompleted ? (
                <Trophy className="w-5 h-5 text-yellow-500" />
              ) : (
                <Target className="w-5 h-5 text-primary" />
              )}
              <CardTitle className="text-base">{challenge.title}</CardTitle>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 text-muted-foreground hover:text-destructive"
              onClick={() => onDelete(challenge.challenge_id)}
              data-testid={`delete-challenge-${challenge.challenge_id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
          <CardDescription className="flex items-center gap-2">
            <Calendar className="w-3 h-3" />
            {t.challenges.months[challenge.month - 1]} {challenge.year}
            {challenge.media_type && (
              <Badge variant="secondary" className="text-xs ml-2">
                {mediaTypes.find(mt => mt.value === challenge.media_type)?.label || challenge.media_type}
              </Badge>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t.challenges.progress}</span>
              <span className="font-semibold">
                {challenge.progress} / {challenge.target_count}
                {isCompleted && <CheckCircle2 className="w-4 h-4 inline ml-1 text-primary" />}
              </span>
            </div>
            <Progress value={progressPercent} className="h-2" />
            {isCompleted ? (
              <p className="text-xs text-primary font-medium">{t.challenges.challengeCompleted}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                {t.challenges.moreToGo(challenge.target_count - challenge.progress)}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export function Challenges({ user }) {
  const { t } = useTranslation();
  const mediaTypes = [
    { value: "", label: t.challenges.allTypes },
    { value: "book", label: t.mediaTypesPlural.book },
    { value: "movie", label: t.mediaTypesPlural.movie },
    { value: "tv_series", label: t.mediaTypesPlural.tv_series },
    { value: "video_game", label: t.mediaTypesPlural.video_game },
  ];
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const [title, setTitle] = useState("");
  const [targetCount, setTargetCount] = useState(5);
  const [mediaType, setMediaType] = useState("");
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());

  useEffect(() => {
    fetchChallenges();
  }, []);

  const fetchChallenges = async () => {
    try {
      const response = await axios.get(`${API}/challenges`);
      setChallenges(response.data.challenges || []);
    } catch (error) {
      console.error("Error fetching challenges:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error(t.challenges.titleRequired);
      return;
    }
    
    setCreating(true);
    try {
      const payload = {
        title: title.trim(),
        target_count: parseInt(targetCount),
        month: parseInt(month),
        year: parseInt(year),
        media_type: mediaType || null
      };
      
      const response = await axios.post(`${API}/challenges`, payload);
      setChallenges(prev => [...prev, response.data]);
      toast.success(t.challenges.created);
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error("Error creating challenge:", error);
      toast.error(error.response?.data?.detail || t.challenges.createError);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (challengeId) => {
    try {
      await axios.delete(`${API}/challenges/${challengeId}`);
      setChallenges(prev => prev.filter(c => c.challenge_id !== challengeId));
      toast.success(t.challenges.deleted);
    } catch (error) {
      console.error("Error deleting challenge:", error);
      toast.error(t.challenges.deleteError);
    }
  };

  const resetForm = () => {
    setTitle("");
    setTargetCount(5);
    setMediaType("");
    setMonth(new Date().getMonth() + 1);
    setYear(new Date().getFullYear());
  };

  const activeChallenges = challenges.filter(c => !c.completed);
  const completedChallenges = challenges.filter(c => c.completed);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="challenges-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t.challenges.title}</h1>
          <p className="text-muted-foreground mt-1">{t.challenges.subtitle}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-challenge-btn">
              <Plus className="w-4 h-4 mr-2" />
              {t.challenges.newChallenge}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t.challenges.createTitle}</DialogTitle>
              <DialogDescription>
                {t.challenges.createDescription}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">{t.challenges.challengeTitle}</Label>
                <Input
                  id="title"
                  placeholder={t.challenges.challengePlaceholder}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  data-testid="challenge-title-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="target">{t.challenges.targetCount}</Label>
                  <Input
                    id="target"
                    type="number"
                    min="1"
                    max="100"
                    value={targetCount}
                    onChange={(e) => setTargetCount(e.target.value)}
                    data-testid="challenge-target-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t.challenges.mediaTypeOptional}</Label>
                  <Select value={mediaType} onValueChange={setMediaType}>
                    <SelectTrigger data-testid="challenge-media-type">
                      <SelectValue placeholder={t.challenges.allTypes} />
                    </SelectTrigger>
                    <SelectContent>
                      {mediaTypes.map(mt => (
                        <SelectItem key={mt.value} value={mt.value || "all"}>
                          {mt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t.challenges.month}</Label>
                  <Select value={month.toString()} onValueChange={(v) => setMonth(parseInt(v))}>
                    <SelectTrigger data-testid="challenge-month">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {t.challenges.months.map((m, i) => (
                        <SelectItem key={i} value={(i + 1).toString()}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t.challenges.year}</Label>
                  <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
                    <SelectTrigger data-testid="challenge-year">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026].map(y => (
                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  {t.common.cancel}
                </Button>
                <Button type="submit" disabled={creating} data-testid="submit-challenge-btn">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {t.challenges.createChallenge}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Target className="w-5 h-5 text-primary" />
          {t.challenges.activeChallenges(activeChallenges.length)}
        </h2>
        {activeChallenges.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {activeChallenges.map((challenge, index) => (
              <ChallengeCard 
                key={challenge.challenge_id} 
                challenge={challenge} 
                onDelete={handleDelete}
                index={index} 
              />
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-8 text-center">
              <Target className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">{t.challenges.noActive}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {completedChallenges.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-500" />
            {t.challenges.completedChallenges(completedChallenges.length)}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {completedChallenges.map((challenge, index) => (
              <ChallengeCard 
                key={challenge.challenge_id} 
                challenge={challenge} 
                onDelete={handleDelete}
                index={index} 
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Challenges;
